import React, { useMemo } from 'react';
import { Button } from 'antd';
import { useMutation, useQuery } from '@common/hooks';
import {
  WORKLOAD_BLOCKS_QUERY,
  CLEAR_WORKLOAD_MUTATION,
  SCHEDULE_DAY_BOUNDARY_QUERY,
} from '@scheduling/graphql';
import { Banner, ErrorHandler, Modal } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import moment from 'moment-timezone';
import { toISODateFormat } from 'works/legacy/lib/date-helper';

export function WorkloadWarningBanner({
  organizationUnitId,
  roleId,
  startDate,
}: {
  organizationUnitId: string | null;
  roleId: string | null;
  startDate: string | null;
}) {
  const { data: boundaryData, error: boundaryError } = useQuery(
    SCHEDULE_DAY_BOUNDARY_QUERY,
    {
      variables: { orgUnitId: organizationUnitId! },
      skip: !organizationUnitId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const boundary = boundaryData?.organizationUnit?.scheduleDayBoundary;

  const formattedDate = startDate ? toISODateFormat(startDate) : null;

  const queryRange = useMemo(
    () =>
      boundary && formattedDate
        ? {
            startsAt: utcDateAtBoundary(formattedDate, boundary).format(),
            endsAt: utcDateAtBoundary(formattedDate, boundary, 1).format(),
          }
        : null,
    [formattedDate, boundary]
  );

  const hasUnitAndRole = !!organizationUnitId && !!roleId;

  const { data: workloadData, error: workloadError } = useQuery(
    WORKLOAD_BLOCKS_QUERY,
    {
      variables: {
        organizationUnitId: organizationUnitId!,
        roleId: roleId!,
        startsAt: queryRange?.startsAt ?? '',
        endsAt: queryRange?.endsAt ?? '',
      },
      skip: !hasUnitAndRole || !queryRange,
      fetchPolicy: 'network-only',
    }
  );

  const hasExistingBlocks = (workloadData?.workloadBlocks?.blocks || []).some(
    (b) => (b.headcount ?? 0) > 0
  );

  const [clearWorkload] = useMutation(CLEAR_WORKLOAD_MUTATION, {
    onCompleted: () => {
      createMessage('success', 'Workload has been cleared.');
    },
    onError: () => {
      createMessage('error', 'Failed to clear workload. Please try again.');
    },
    refetchQueries: ['ShiftsCountByDay', 'ShiftGapsByDay', 'WorkloadBlocks'],
  });

  const handleClearWorkload = () => {
    if (!queryRange) return;

    Modal.confirm({
      title: 'Confirm clearing workload',
      content:
        'Clearing workload will delete all open shifts associated with the existing workload.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () =>
        clearWorkload({
          variables: {
            organizationUnitId: organizationUnitId!,
            roleId: roleId!,
            startsAt: queryRange.startsAt,
            endsAt: queryRange.endsAt,
          },
        }),
    });
  };

  if (boundaryError || workloadError) {
    return (
      <ErrorHandler
        error={boundaryError || workloadError}
        messageType="contextual"
        log="WorkloadWarningBanner"
      />
    );
  }

  if (!hasExistingBlocks) return null;

  return (
    <Banner type="warning" style={{ width: '100%' }}>
      This unit has an active workload for this date. Creating manual shifts may
      conflict with workload-generated shifts.{' '}
      <Button type="link" style={{ padding: 0 }} onClick={handleClearWorkload}>
        Clear Workload
      </Button>
    </Banner>
  );
}

function utcDateAtBoundary(date: string, boundary: string, addDays = 0) {
  const hour = moment(boundary, 'HH:mm').hour();
  return moment.utc(date).add(addDays, 'days').startOf('day').hour(hour);
}
